/* "The Weather Doesn't Wait" — the 45-second weather commercial.

   Built entirely in-engine: the storm plates are stills that are pushed, parallaxed, rain-streaked
   and lightning-lit frame by frame, and every product surface is drawn as live UI rather than a
   screenshot, so no real property, owner or personal name ever appears in the ad.

   Like the first commercial, every pixel is a pure function of t — no CSS animation, no timers —
   which is what makes the offline render frame-exact. */
import { BrandBadge } from "@/components/BrandMark";
import { CloudLightning, MapPin, Bell, ClipboardCheck, Zap, Check } from "lucide-react";

const PLATE = "/ad2/storm";
const NAVY = "#070c1e";
const GOLD = "#d4af37";
const ALERT = "#ff6b4a";

const clamp = (v, a = 0, b = 1) => Math.min(b, Math.max(a, v));
const seg = (v, a, b) => {
  const x = clamp((v - a) / Math.max(b - a, 0.0001));
  return x * x * (3 - 2 * x);
};
// deterministic pseudo-random, so rain is identical on every render pass
const rnd = (i) => {
  const s = Math.sin(i * 12.9898) * 43758.5453;
  return s - Math.floor(s);
};

/* A still, made to move: slow push plus a drift, so it reads as footage rather than a photo. */
const Plate = ({ name, p, tall, push = 0.1, dim = 0.35, drift = 2 }) => (
  <div className="absolute inset-0 overflow-hidden">
    <img src={`${PLATE}/${tall ? name.tall : name.wide}.jpg`} alt="" aria-hidden
      className="absolute inset-0 h-full w-full object-cover"
      style={{ transform: `scale(${1.08 + p * push}) translate(${(p - 0.5) * drift}%, ${(p - 0.5) * -drift * 0.6}%)`,
               filter: "saturate(0.9) contrast(1.12) brightness(0.92)" }} />
    <div className="absolute inset-0" style={{ background: `rgba(6,11,26,${dim})` }} />
  </div>
);

/* Rain drawn as streaks, and a wind-driven sheet that crosses the frame. */
const Rain = ({ t, u, count = 90, opacity = 0.5 }) => (
  <div className="absolute inset-0 overflow-hidden" style={{ pointerEvents: "none" }}>
    {Array.from({ length: count }).map((_, i) => {
      const speed = 1.1 + rnd(i) * 1.5;
      const x = rnd(i + 7) * 108 - 4;
      const len = (2.4 + rnd(i + 13) * 5) * u;
      const y = ((t * speed * 120 + rnd(i + 21) * 140) % 140) - 20;
      return (
        <span key={i} style={{
          position: "absolute", left: `${x}%`, top: `${y}%`,
          width: Math.max(1, 0.12 * u), height: len,
          background: `linear-gradient(to bottom, rgba(210,228,255,0), rgba(210,228,255,${opacity}))`,
          transform: "rotate(11deg)",
        }} />
      );
    })}
  </div>
);

const Lightning = ({ t, at = [], u }) => {
  // a double flash, the way real lightning fires
  const flash = at.reduce((acc, a) => {
    const d = t - a;
    if (d < 0 || d > 0.55) return acc;
    const f = Math.max(Math.exp(-d * 16), d > 0.16 && d < 0.3 ? 0.55 : 0);
    return Math.max(acc, f);
  }, 0);
  if (flash <= 0.01) return null;
  return (
    <div className="absolute inset-0" style={{ pointerEvents: "none",
      background: `radial-gradient(120% 80% at 50% 10%, rgba(214,235,255,${flash * 0.85}),
                   rgba(160,196,255,${flash * 0.25}) 45%, transparent 75%)`,
      mixBlendMode: "screen" }} />
  );
};

const Kicker = ({ children, u, o = 1, color = GOLD }) => (
  <p style={{ color, opacity: o, fontSize: `${1.85 * u}px`, fontWeight: 700,
              letterSpacing: `${0.5 * u}px`, textTransform: "uppercase" }}>{children}</p>
);

/* A panel that looks like the product without being a screenshot of anyone's data. */
const Panel = ({ children, u, style = {}, o = 1, y = 0 }) => (
  <div style={{ background: "rgba(13,20,46,0.92)", border: "1px solid rgba(212,175,55,0.24)",
                borderRadius: `${1.5 * u}px`, padding: `${2 * u}px`, opacity: o,
                transform: `translateY(${y}px)`,
                boxShadow: "0 24px 60px rgba(0,0,0,0.5)", ...style }}>
    {children}
  </div>
);

/* ---- 0-5s · the threat ---------------------------------------------------------------------- */
const Threat = ({ p, t, u, tall }) => (
  <div className="absolute inset-0 overflow-hidden" style={{ background: NAVY }}>
    <Plate name={{ wide: "coast", tall: "clouds" }} p={p} tall={tall} dim={0.42} push={0.14} />
    <Rain t={t} u={u} count={110} opacity={0.55} />
    <Lightning t={t} at={[1.35, 3.6]} u={u} />
    <div className="absolute inset-0" style={{ background:
      "linear-gradient(to top, rgba(4,8,20,0.94) 6%, rgba(4,8,20,0.2) 48%, rgba(4,8,20,0.5) 100%)" }} />
    <div className="absolute inset-0 flex flex-col items-center justify-center text-center"
         style={{ padding: "8%" }}>
      <h1 className="font-display" style={{
        color: "#fff", fontWeight: 800, lineHeight: 0.98, fontSize: `${8.6 * u}px`,
        letterSpacing: `${-0.12 * u}px`,
        opacity: seg(p, 0.1, 0.34),
        transform: `scale(${1.06 - seg(p, 0.1, 0.9) * 0.06})`,
        textShadow: "0 8px 40px rgba(0,0,0,0.75)" }}>
        WEATHER<br />DOESN'T<br /><span style={{ color: GOLD }}>WAIT.</span>
      </h1>
    </div>
  </div>
);

/* ---- 5-12s · the problem ------------------------------------------------------------------- */
const RADAR_CELLS = [[52, 46, 15], [62, 38, 9], [44, 58, 11], [70, 56, 7]];

const Problem = ({ p, t, u, tall }) => {
  const sweep = (t * 90) % 360;
  return (
    <div className="absolute inset-0 overflow-hidden" style={{ background: NAVY }}>
      <Plate name={{ wide: "road", tall: "lightning" }} p={p} tall={tall} dim={0.58} push={0.1} />
      <Rain t={t} u={u} count={80} opacity={0.4} />
      <Lightning t={t} at={[2.6]} u={u} />
      <div className="absolute inset-0" style={{ background:
        "linear-gradient(to top, rgba(4,8,20,0.96) 20%, rgba(4,8,20,0.35) 70%, rgba(4,8,20,0.55) 100%)" }} />

      {/* radar, drawn: cells, rings and a sweeping arm */}
      <div style={{ position: "absolute", left: "50%", top: tall ? "26%" : "22%",
                    width: `${42 * u}px`, height: `${42 * u}px`, marginLeft: `${-21 * u}px`,
                    opacity: seg(p, 0.08, 0.3) }}>
        <div className="absolute inset-0" style={{ borderRadius: "50%",
          border: `1px solid rgba(120,170,255,0.35)`,
          background: "radial-gradient(circle, rgba(18,34,80,0.55), rgba(8,14,34,0.2) 70%, transparent)" }} />
        {[0.66, 0.33].map((r) => (
          <div key={r} className="absolute" style={{
            left: `${50 - r * 50}%`, top: `${50 - r * 50}%`, width: `${r * 100}%`, height: `${r * 100}%`,
            borderRadius: "50%", border: "1px solid rgba(120,170,255,0.22)" }} />
        ))}
        {RADAR_CELLS.map(([cx, cy, r], i) => (
          <div key={i} className="absolute" style={{
            left: `${cx - r}%`, top: `${cy - r}%`, width: `${r * 2}%`, height: `${r * 2}%`,
            borderRadius: "50%", filter: `blur(${0.8 * u}px)`,
            background: `radial-gradient(circle, rgba(255,107,74,${0.75 - i * 0.12}),
                         rgba(255,180,60,0.35) 55%, transparent 75%)`,
            opacity: seg(p, 0.16 + i * 0.07, 0.34 + i * 0.07) }} />
        ))}
        <div className="absolute inset-0" style={{ borderRadius: "50%", overflow: "hidden" }}>
          <div className="absolute" style={{
            left: "50%", top: "50%", width: "50%", height: Math.max(1, 0.3 * u),
            transformOrigin: "0% 50%", transform: `rotate(${sweep}deg)`,
            background: "linear-gradient(90deg, rgba(170,215,255,0.9), rgba(170,215,255,0))" }} />
        </div>
      </div>

      <div className="absolute inset-x-0 bottom-0" style={{ padding: "8%" }}>
        <Kicker u={u} o={seg(p, 0, 0.14)} color="#8fb4ff">Conditions changing</Kicker>
        <h2 className="font-display" style={{
          marginTop: `${1.8 * u}px`, color: "#fff", fontWeight: 800, fontSize: `${5.2 * u}px`,
          lineHeight: 1.08, opacity: seg(p, 0.06, 0.26),
          transform: `translateY(${(1 - seg(p, 0.06, 0.26)) * 2.4 * u}px)` }}>
          When conditions change,<br />
          <span style={{ color: GOLD }}>your operation changes with them.</span>
        </h2>
      </div>
    </div>
  );
};

/* ---- 12-25s · the platform takes over ------------------------------------------------------- */
const PINS = [
  ["Oceanside 12", 24, 34, true], ["Dune Ridge 4", 58, 26, false], ["Sound View 7", 40, 58, false],
  ["Pier Street 3", 72, 62, false], ["Harbor Row 5", 18, 72, false], ["Lighthouse 9", 82, 40, false],
];
const STAGES = [
  ["Weather alert", CloudLightning], ["Property identified", MapPin],
  ["Automation triggered", Zap], ["Team notified", Bell],
];

const Chain = ({ p, t, u, tall }) => {
  const mapIn = seg(p, 0.02, 0.2);
  // Whole-pixel drift only: a fractional translate makes 1px borders and text crawl, which is
  // seen as the panel shimmering rather than as motion.
  const float = Math.round(Math.sin(t * 0.6) * 0.35 * u);
  return (
    <div className="absolute inset-0 overflow-hidden" style={{ background: NAVY }}>
      <div className="absolute inset-0" style={{ background:
        "radial-gradient(90% 70% at 50% 18%, rgba(24,44,104,0.75), rgba(6,11,26,1) 72%)" }} />
      {/* the grid the command centre sits on */}
      <div className="absolute inset-0" style={{ opacity: 0.35, backgroundSize: `${5 * u}px ${5 * u}px`,
        backgroundImage:
          "linear-gradient(rgba(120,170,255,0.09) 1px, transparent 1px)," +
          "linear-gradient(90deg, rgba(120,170,255,0.09) 1px, transparent 1px)" }} />

      {/* map of properties — neutral names, no owner data */}
      <div style={{ position: "absolute", left: "8%", right: "8%",
                    top: tall ? "12%" : "8%", height: tall ? "40%" : "46%",
                    opacity: mapIn,
                    transform: `translateY(${float}px)`,
                    border: "1px solid rgba(120,170,255,0.22)", borderRadius: `${1.6 * u}px`,
                    background: "rgba(9,16,38,0.6)", overflow: "hidden" }}>
        <p style={{ position: "absolute", left: `${1.8 * u}px`, top: `${1.4 * u}px`,
                    color: "#8fb4ff", fontSize: `${1.5 * u}px`, fontWeight: 700,
                    letterSpacing: `${0.4 * u}px`, textTransform: "uppercase" }}>
          Portfolio · live conditions
        </p>
        {/* the weather cell crossing the map */}
        <div className="absolute" style={{
          left: `${6 + seg(p, 0.1, 0.9) * 26}%`, top: "18%", width: `${30 * u}px`, height: `${30 * u}px`,
          borderRadius: "50%", filter: `blur(${1.6 * u}px)`, opacity: 0.55 * seg(p, 0.08, 0.3),
          background: "radial-gradient(circle, rgba(255,107,74,0.85), rgba(255,180,60,0.3) 55%, transparent 72%)" }} />
        {PINS.map(([label, x, y, hot], i) => {
          const on = seg(p, 0.06 + i * 0.035, 0.18 + i * 0.035);
          const flagged = hot ? seg(p, 0.3, 0.42) : 0;
          return (
            <div key={label} style={{ position: "absolute", left: `${x}%`, top: `${y}%`,
                                      opacity: on, transform: "translate(-50%, -50%)" }}>
              {flagged > 0 && (
                <div style={{ position: "absolute", left: "50%", top: "50%",
                  width: `${(6 + flagged * 6) * u}px`, height: `${(6 + flagged * 6) * u}px`,
                  marginLeft: `${-(3 + flagged * 3) * u}px`, marginTop: `${-(3 + flagged * 3) * u}px`,
                  borderRadius: "50%", border: `2px solid rgba(255,107,74,${0.8 - flagged * 0.5})` }} />
              )}
              <div style={{ display: "flex", alignItems: "center", gap: `${0.7 * u}px`,
                            background: flagged > 0.2 ? "rgba(255,107,74,0.9)" : "rgba(13,20,46,0.9)",
                            border: `1px solid ${flagged > 0.2 ? ALERT : "rgba(120,170,255,0.35)"}`,
                            borderRadius: `${0.9 * u}px`, padding: `${0.55 * u}px ${1 * u}px` }}>
                <MapPin style={{ width: 1.5 * u, height: 1.5 * u,
                                 color: flagged > 0.2 ? "#fff" : "#8fb4ff" }} />
                <span style={{ color: "#fff", fontSize: `${1.35 * u}px`, fontWeight: 700 }}>{label}</span>
              </div>
            </div>
          );
        })}
      </div>

      {/* the chain of consequence */}
      <div style={{ position: "absolute", left: "8%", right: "8%", bottom: tall ? "10%" : "8%" }}>
        <Kicker u={u} o={seg(p, 0.24, 0.4)}>Weather intelligence → action</Kicker>
        <div style={{ display: "grid", gap: `${1 * u}px`, marginTop: `${1.6 * u}px`,
                      gridTemplateColumns: tall ? "1fr" : "repeat(4, 1fr)" }}>
          {STAGES.map(([label, Icon], i) => {
            const on = seg(p, 0.34 + i * 0.12, 0.46 + i * 0.12);
            return (
              <div key={label} style={{
                display: "flex", alignItems: "center", gap: `${1 * u}px`,
                borderRadius: `${1.1 * u}px`, padding: `${1.2 * u}px ${1.4 * u}px`,
                background: `rgba(212,175,55,${0.06 + on * 0.12})`,
                border: `1px solid rgba(212,175,55,${0.2 + on * 0.6})`,
                opacity: 0.25 + on * 0.75,
                transform: `translateY(${(1 - on) * 1.6 * u}px)` }}>
                <Icon style={{ width: 2 * u, height: 2 * u, color: GOLD, flexShrink: 0 }} />
                <span style={{ color: "#fff", fontSize: `${1.75 * u}px`, fontWeight: 700 }}>{label}</span>
                <Check style={{ width: 1.6 * u, height: 1.6 * u, color: GOLD, marginLeft: "auto",
                                opacity: seg(p, 0.42 + i * 0.12, 0.52 + i * 0.12) }} />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

/* ---- 25-35s · the automation does the work -------------------------------------------------- */
const Automate = ({ p, t, u, tall, line, lineAlpha = 1 }) => {
  const card = seg(p, 0.04, 0.24);
  const push = seg(p, 0.42, 0.62);
  const float = Math.round(Math.sin(t * 0.55) * 0.3 * u);
  return (
    <div className="absolute inset-0 overflow-hidden" style={{ background: NAVY }}>
      <div className="absolute inset-0" style={{ background:
        "radial-gradient(85% 65% at 50% 30%, rgba(24,44,104,0.7), rgba(6,11,26,1) 74%)" }} />
      {/* The stack is pinned, not centred. When the headline below was in the same centred
          column, every line change altered the column height and shunted the panels up or down —
          that jump is what read as the pop-ups glitching hard. */}
      <div className="absolute inset-x-0" style={{ top: tall ? "14%" : "9%", padding: "0 8%" }}>
        <Kicker u={u}>Automation running</Kicker>

        <Panel u={u} o={card} y={Math.round((1 - card) * 2.4 * u) + float} style={{ marginTop: `${1.8 * u}px` }}>
          <div style={{ display: "flex", alignItems: "center", gap: `${1 * u}px` }}>
            <ClipboardCheck style={{ width: 2.2 * u, height: 2.2 * u, color: GOLD }} />
            <span style={{ color: "#fff", fontWeight: 800, fontSize: `${2.2 * u}px` }}>
              Storm inspection created
            </span>
            <span style={{ marginLeft: "auto", color: GOLD, fontWeight: 700,
                           fontSize: `${1.5 * u}px`, letterSpacing: `${0.3 * u}px` }}>AUTO</span>
          </div>
          {[["Property", "Oceanside 12"], ["Trigger", "High wind advisory"],
            ["Assigned", "On-call crew lead"], ["Due", "Before next arrival"]].map(([k, v], i) => {
            const row = seg(p, 0.14 + i * 0.05, 0.26 + i * 0.05);
            return (
              <div key={k} style={{ display: "flex", justifyContent: "space-between",
                                    marginTop: `${1.1 * u}px`, opacity: row }}>
                <span style={{ color: "rgba(180,198,240,0.75)", fontSize: `${1.65 * u}px`,
                               fontWeight: 600 }}>{k}</span>
                <span style={{ color: "#fff", fontSize: `${1.65 * u}px`, fontWeight: 700 }}>{v}</span>
              </div>
            );
          })}
        </Panel>

        <Panel u={u} o={push} y={Math.round((1 - push) * 3 * u) - float}
               style={{ marginTop: `${1.6 * u}px`, borderColor: "rgba(120,170,255,0.4)",
                        background: "rgba(16,26,60,0.95)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: `${1.1 * u}px` }}>
            <div style={{ width: `${3.4 * u}px`, height: `${3.4 * u}px`, borderRadius: `${0.9 * u}px`,
                          background: GOLD, display: "flex", alignItems: "center",
                          justifyContent: "center", flexShrink: 0 }}>
              <Bell style={{ width: 2 * u, height: 2 * u, color: "#101a3d" }} />
            </div>
            <div>
              <p style={{ color: "#fff", fontWeight: 800, fontSize: `${1.85 * u}px` }}>
                Cornerstone Ops
              </p>
              <p style={{ color: "rgba(200,216,255,0.85)", fontSize: `${1.6 * u}px`,
                          fontWeight: 600, marginTop: `${0.3 * u}px` }}>
                Wind advisory — inspection assigned to you
              </p>
            </div>
          </div>
        </Panel>
      </div>

      {/* Fixed block at the foot of the frame: the text inside it can change length freely
          without moving anything else. */}
      <div className="absolute inset-x-0 flex items-start"
           style={{ bottom: tall ? "12%" : "8%", height: `${9 * u}px`, padding: "0 8%" }}>
        <p className="font-display" style={{
          color: GOLD, fontWeight: 800, fontSize: `${3.4 * u}px`, lineHeight: 1.12,
          opacity: lineAlpha, transform: `translateY(${Math.round((1 - lineAlpha) * 0.8 * u)}px)` }}>
          {line}
        </p>
      </div>
    </div>
  );
};

/* ---- 35-40s · the hook ---------------------------------------------------------------------- */
const Hook = ({ p, t, u, tall }) => {
  const flip = seg(p, 0.46, 0.62);
  return (
    <div className="absolute inset-0 overflow-hidden" style={{ background: NAVY }}>
      <div className="absolute inset-0" style={{ opacity: 1 - flip }}>
        <Plate name={{ wide: "houses", tall: "lightning" }} p={p} tall={tall} dim={0.45} push={0.12} />
        <Rain t={t} u={u} count={100} opacity={0.5} />
        <Lightning t={t} at={[0.7, 2.2]} u={u} />
      </div>
      <div className="absolute inset-0" style={{ opacity: flip, background:
        "radial-gradient(85% 65% at 50% 40%, rgba(24,44,104,0.8), rgba(6,11,26,1) 72%)" }} />
      <div className="absolute inset-0 flex flex-col items-center justify-center text-center"
           style={{ padding: "8%",
                    background: `linear-gradient(to top, rgba(4,8,20,${0.9 - flip * 0.4}) 10%, rgba(4,8,20,0.25) 70%)` }}>
        <h2 className="font-display" style={{
          color: "#fff", fontWeight: 800, fontSize: `${5.6 * u}px`, lineHeight: 1.08,
          opacity: seg(p, 0.05, 0.24), textShadow: "0 8px 40px rgba(0,0,0,0.7)" }}>
          Don't wait for the storm<br />to tell you there's a problem.
        </h2>
        <h2 className="font-display" style={{
          marginTop: `${2.2 * u}px`, color: GOLD, fontWeight: 800, fontSize: `${6.4 * u}px`,
          lineHeight: 1.05, opacity: seg(p, 0.5, 0.72),
          transform: `translateY(${(1 - seg(p, 0.5, 0.78)) * 2.2 * u}px)` }}>
          Let your operation<br />know first.
        </h2>
      </div>
    </div>
  );
};

/* ---- 40-45s · brand, CTA, verse ------------------------------------------------------------- */
const Brand = ({ p, t, u }) => {
  const a = seg(p, 0.02, 0.2);
  const b = seg(p, 0.16, 0.36);
  const c = seg(p, 0.34, 0.54);
  const verse = seg(p, 0.6, 0.84);
  const float = Math.round(Math.sin(t * 0.5) * 0.28 * u);
  return (
    <div className="absolute inset-0 flex flex-col items-center justify-center text-center"
         style={{ background: NAVY, padding: "8%" }}>
      <div className="absolute inset-0" style={{ background:
        "radial-gradient(80% 58% at 50% 42%, rgba(38,66,152,0.62), rgba(6,11,26,1) 74%)" }} />
      <div style={{ position: "relative", transform: `translateY(${float}px)` }}>
        <p className="font-display" style={{ color: "#fff", fontWeight: 800, fontSize: `${4.4 * u}px`,
          lineHeight: 1.12, opacity: a }}>
          KNOW WHAT'S COMING.
        </p>
        <p className="font-display" style={{ color: GOLD, fontWeight: 800, fontSize: `${4.4 * u}px`,
          lineHeight: 1.12, opacity: b }}>
          AUTOMATE WHAT HAPPENS NEXT.
        </p>
        <div style={{ opacity: c, marginTop: `${3.4 * u}px` }}>
          <div className="flex justify-center">
            <div style={{ width: `${10 * u}px`, height: `${10 * u}px` }}>
              <BrandBadge size="h-full w-full" icon="h-3/5 w-3/5" />
            </div>
          </div>
          <h2 className="font-display" style={{ marginTop: `${2 * u}px`, color: "#fff",
            fontWeight: 800, fontSize: `${6.8 * u}px`, lineHeight: 1 }}>
            Cornerstone Ops
          </h2>
          <div style={{ height: 1, width: `${24 * u}px`, margin: `${2 * u}px auto`,
                        background: `linear-gradient(90deg, transparent, ${GOLD}, transparent)` }} />
          <p style={{ color: GOLD, fontSize: `${2.1 * u}px`, fontWeight: 700,
                      letterSpacing: `${0.6 * u}px`, textTransform: "uppercase" }}>
            The Operational Command Center
          </p>
          <p style={{ marginTop: `${2.4 * u}px`, color: "#fff", fontWeight: 700,
                      fontSize: `${2.4 * u}px` }}>
            Run your operation from one command center.
          </p>
          <p style={{ marginTop: `${1 * u}px`, color: "rgba(200,216,255,0.8)", fontWeight: 700,
                      fontSize: `${2.2 * u}px` }}>
            www.cornerstoneops.io
          </p>
        </div>
      </div>
      <div style={{ position: "absolute", left: 0, right: 0, bottom: `${6 * u}px`, opacity: verse,
                    padding: "0 8%" }}>
        <div style={{ height: 1, width: `${18 * u}px`, margin: `0 auto ${1.4 * u}px`,
                      background: `linear-gradient(90deg, transparent, ${GOLD}, transparent)` }} />
        <p style={{ color: GOLD, fontSize: `${1.5 * u}px`, fontWeight: 700,
                    letterSpacing: `${0.45 * u}px`, textTransform: "uppercase" }}>Psalm 24:1</p>
        <p style={{ marginTop: `${0.8 * u}px`, color: "rgba(216,228,255,0.85)",
                    fontSize: `${2 * u}px`, fontStyle: "italic" }}>
          "The earth is the LORD'S, and everything in it."
        </p>
      </div>
    </div>
  );
};

/* Four spoken lines share the automation beat, so its headline follows the voice. */
const AUTOMATE_LINES = {
  affect: "Know which properties are affected.",
  trigger: "Trigger the right workflows.",
  alert: "Alert the right people.",
  moving: "And keep your operation moving.",
};

const SCENE_FOR = {
  threat: "threat", problem: "problem", chain: "chain",
  affect: "automate", trigger: "automate", alert: "automate", moving: "automate",
  hook: "hook", first: "hook", brand: "brand", cta: "brand",
};

const RENDERERS = { threat: Threat, problem: Problem, chain: Chain, automate: Automate,
                    hook: Hook, brand: Brand };

export default function StormFilm({ t = 0, segments = [], cue = null, w = 1080, h = 1080 }) {
  const u = Math.min(w, h) / 100;
  const tall = h / w > 1.25;
  const idx = segments.reduce((best, s, i) => (t >= s.start ? i : best), 0);
  const active = segments[idx];
  if (!active) return <div className="absolute inset-0" style={{ background: NAVY }} />;
  const Scene = RENDERERS[active.scene] || Threat;
  const p = clamp((t - active.start) / Math.max(active.end - active.start, 0.001));
  const line = AUTOMATE_LINES[cue?.scene] || "";
  /* The four automation lines share one beat. Swapping their text instantly popped, so each one
     fades in and out around its own cue. */
  const lineAlpha = cue
    ? Math.min(seg(t, cue.start, cue.start + 0.22), 1 - seg(t, cue.end - 0.1, cue.end + 0.3))
    : 0;

  /* A short dip up from the film's own black rather than a crossfade between two scenes: holding
     the outgoing scene underneath left a double image for a third of a second, which is what
     reads as the panels glitching. Each scene also dips out at its tail, so cuts dissolve. */
  const rise = clamp((t - active.start) / 0.3);
  const fall = 1 - clamp((t - (active.end - 0.2)) / 0.2);
  const into = Math.min(rise, fall);

  return (
    <div className="absolute inset-0 overflow-hidden" style={{ background: NAVY }}
         data-testid={`storm-scene-${active.scene}`}>
      <div className="absolute inset-0" style={{ opacity: into }}>
        <Scene p={p} t={t - active.start} u={u} w={w} h={h} tall={tall}
               line={line} lineAlpha={lineAlpha} />
      </div>
      {/* A slow, continuous light drift. Without it the UI beats sit perfectly still once their
          eases finish, and a run of identical frames reads as a freeze rather than a hold.
          It lives behind the type, on a soft gradient, so it can move sub-pixel safely. */}
      <div className="absolute inset-0" style={{ pointerEvents: "none", opacity: 0.5, background:
        `radial-gradient(60% 45% at ${(50 + 9 * Math.sin(t * 0.21)).toFixed(2)}% ${(44 + 7 * Math.cos(t * 0.17)).toFixed(2)}%,
         rgba(96,140,255,0.07), transparent 70%)` }} />
      <div className="absolute inset-0" style={{ opacity: 0.045, pointerEvents: "none",
        backgroundImage: "radial-gradient(rgba(255,255,255,0.4) 1px, transparent 1px)",
        backgroundSize: `${0.6 * u}px ${0.6 * u}px` }} />
    </div>
  );
}

export { SCENE_FOR };
